import { useEffect, useRef } from 'react'

import { useParams } from 'next/navigation'

import dayjs from 'lib/dayjs'
import { useTranslation } from 'app/i18n/client'

import Button from '../../../Button'

import GuidelinePopup from '../../GuidelinePopup'
import { getJapanTimeZoneToday } from 'utils/toJapanTimeZone'
import { setDateStyles } from '../../utils'
import DateRangeSelect from '../../DateRangeSelect'
import Loading from 'app/components/atoms/Loading'

interface CalendarSelectProps {
  currentActiveStartDate: Date
  selectedValue: (Date | null)[]
  disabledDates?: Date[]
  ebisuDisabledDates?: Date[]
  isLoading?: boolean
  setCurrentActiveStartDate: (value: Date) => void
  handleReset: () => void
  handleCancel: () => void
  handleConfirm: () => void
  onSelectValue: (value: (Date | null)[]) => void
  onOpenMaxNightModal: () => void
}

const CalendarSelect: React.FC<CalendarSelectProps> = ({
  currentActiveStartDate,
  selectedValue,
  setCurrentActiveStartDate,
  isLoading,
  disabledDates,
  ebisuDisabledDates,
  handleCancel,
  handleReset,
  handleConfirm,
  onSelectValue,
  onOpenMaxNightModal,
}) => {
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const [selectedFrom, selectedTo] = selectedValue

  const today = getJapanTimeZoneToday().toDate()

  const calendarRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setDateStyles(calendarRef, today)
  }, [calendarRef?.current])

  const formatDate = (date?: Date | null, locale?: string) => {
    if (!date || !locale) return t('Select Date')

    return dayjs(date)
      .locale(['zh-Hans', 'zh-Hant'].includes(locale) ? 'zh-cn' : locale)
      .format('LL')
  }

  return (
    <div className="flex flex-col bg-white shadow-xl transform transition-all items-center w-full max-h-[100dvh] rounded-t-2.5xl">
      <div className="px-8 w-full">
        <div className="mt-6 w-full flex justify-around pt-3 pb-1">
          <div>
            <label className="text-xs text-gray-50 uppercase font-light">
              {t('Check-in Date')}
            </label>
            <div className="font-lora text-base font-light uppercase">
              {formatDate(selectedFrom, locale)}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-50 uppercase font-light">
              {t('Check-out Date')}
            </label>
            <div className="font-lora text-base font-light uppercase">
              {formatDate(selectedTo, locale)}
            </div>
          </div>
        </div>
        <div className="border-b border-secondary w-full"></div>
      </div>
      <div className="w-full flex-1 min-h-0 overflow-y-auto md:border-t border-gray-800 text-left">
        <div className="w-full flex flex-col flex-1 items-center relative">
          {isLoading && (
            <div className="absolute top-0 left-0 w-full h-full bg-[#F6F6F6] bg-opacity-80 z-[500] flex items-center justify-center">
              <Loading
                className="!text-[#F6F6F6] text-opacity-80"
                baseClassName="!text-primary"
              />
            </div>
          )}
          <div className="mt-4 px-2 w-full">
            <DateRangeSelect
              calendarRef={calendarRef}
              currentActiveStartDate={currentActiveStartDate}
              selectedValue={selectedValue}
              today={today}
              disabledDates={disabledDates}
              ebisuDisabledDates={ebisuDisabledDates}
              onSelectValue={onSelectValue}
              handleCancel={handleCancel}
              setCurrentActiveStartDate={setCurrentActiveStartDate}
              onOpenMaxNightModal={onOpenMaxNightModal}
            />
          </div>
        </div>
      </div>
      {/* Buttons and the day-use/weekly/monthly guideline sit OUTSIDE the
          scroll area: they used to be inside it, so on a viewport shorter than
          the sheet (<~600px) the guideline scrolled out of sight with nothing
          on screen to hint that it was there. Only the calendar scrolls now. */}
      <div className="w-full shrink-0">
        <div className="px-8 py-4 w-full">
          <div className="flex justify-between space-x-6 w-full">
            <Button
              onClick={handleReset}
              variant="white"
              className="select-none uppercase !w-full !px-4"
            >
              {t('Close this modal')}
            </Button>
            <Button
              onClick={handleConfirm}
              className="select-none !bg-primary uppercase !w-full !px-4"
            >
              {t('Confirm')}
            </Button>
          </div>
        </div>
        <div className="pt-2 pb-4 flex items-center justify-center">
          <GuidelinePopup />
        </div>
      </div>
    </div>
  )
}

export default CalendarSelect
